import {SPECS} from './../specs';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {
  CheckoutNavigationService,
  DynamicPaymentMethodsShape,
  DynamicPaymentMethodsTheme,
  ModalType,
  QueryParamsService,
  SiteStore,
} from '@wix/wixstores-client-storefront-sdk';
import {CartService} from '../services/CartService';
import {PaymentBreakdown} from '@wix/cashier-express-checkout-widget/dist/src/types/PaymentBreakdown';
import {EcommerceSettingsService} from '../services/EcommerceSettingsService';
import {CreateCheckoutExceptions} from '../../common/constants';
import {ButtonStyle, Theme} from '@wix/cashier-express-checkout-widget/dist/src/types/Styles';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {BIService} from '../services/BIService';
import {OrderService} from '../services/OrderService';
import {ICartControllerApi} from '../../types/app.types';
import {NavigationService} from '../services/NavigationService';
import {ModalManagerService} from '../services/ModalManagerService';
import {MinimumOrderAmountService} from '../services/MinimumOrderAmountService';
import {
  createExpressCheckoutButtonsController,
  ExpressCheckoutButtonsController,
  ExpressCheckoutButtonsState,
} from '@wix/ecom-platform-express-checkout-buttons';
import {openNoOnlinePaymentsModal} from '../utils/openNoOnlinePaymentsModal';
import {getAdditionalFeesPrice, getCatalogAppIds, getNumberOfAdditionalFees} from '../utils/bi.utils';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {isCartV2} from '../utils/isCartV2.utils';

export interface PaymentBreakdownAndAmount {
  paymentBreakdown: PaymentBreakdown;
  paymentAmount: string;
}

export class CashierExpressStore {
  private readonly cartService: CartService;
  private readonly navigationService: NavigationService;
  private readonly checkoutNavigationService: CheckoutNavigationService;
  private readonly ecommerceSettingsService: EcommerceSettingsService;
  private readonly biService: BIService;
  private readonly orderService: OrderService;
  private readonly modalManagerService: ModalManagerService;
  private readonly minimumOrderAmountService: MinimumOrderAmountService;
  private readonly controllerApi: ICartControllerApi;
  private controller: ExpressCheckoutButtonsController;
  private areExpressCheckoutButtonsLoaded: boolean = false;

  constructor(
    controllerParams: ControllerParams,
    controllerApi: ICartControllerApi,
    private readonly siteStore: SiteStore,
    private readonly viewerContext: ViewerContext,
    {
      cartService,
      checkoutNavigationService,
      ecommerceSettingsService,
      biService,
      orderService,
      navigationService,
      modalManagerService,
      minimumOrderAmountService,
    }: {
      cartService: CartService;
      checkoutNavigationService: CheckoutNavigationService;
      ecommerceSettingsService: EcommerceSettingsService;
      biService: BIService;
      orderService: OrderService;
      navigationService: NavigationService;
      modalManagerService: ModalManagerService;
      minimumOrderAmountService: MinimumOrderAmountService;
    },
    private readonly styleSettingsService: StyleSettingsService,
    private readonly origin?: string,
    private readonly widgetId?: string
  ) {
    this.cartService = cartService;
    this.checkoutNavigationService = checkoutNavigationService;
    this.navigationService = navigationService;
    this.ecommerceSettingsService = ecommerceSettingsService;
    this.orderService = orderService;
    this.biService = biService;
    this.modalManagerService = modalManagerService;
    this.minimumOrderAmountService = minimumOrderAmountService;
    this.controllerApi = controllerApi;

    void this.initializeExpressCheckoutButtonsController(controllerParams);
  }

  public init() {
    /* istanbul ignore else */
    if (this.styleSettingsService.isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode) {
      void this.controller.init();
    }
  }

  private readonly initializeExpressCheckoutButtonsController = (controllerParams: ControllerParams) => {
    this.controller = (() => {
      const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
      const editorMode = moveCartToViewerContext
        ? this.viewerContext.getViewMode() === 'Editor'
        : this.siteStore.isEditorMode();

      return createExpressCheckoutButtonsController({
        demoMode: editorMode,
        controllerConfig: controllerParams.controllerConfig,
        httpClient: controllerParams.flowAPI.httpClient,
        onClick: () => {
          this.navigationService.isNavigationToCheckoutInProcess = true;

          this.biService.clickOnCheckoutWithEWallet(
            this.cartService.cartModel,
            this.cartService.estimatedTotals,
            this.cartService.cartType
          );

          const {canCheckout, modalType} = this.checkIsAllowedToCheckout();
          if (!canCheckout) {
            const destination = this.cartService.cartModel.contactInfo?.address;
            const catalogAppId = getCatalogAppIds(this.cartService.cartModel);
            const additionalFeesPrice = getAdditionalFeesPrice(this.cartService.estimatedTotals);
            const additionalFeesNumber = getNumberOfAdditionalFees(this.cartService.estimatedTotals);
            void this.checkoutNavigationService.openLegacyCartModal(
              modalType,
              {destination, catalogAppId, additionalFeesPrice, additionalFeesNumber},
              this.styleSettingsService.isEditorX
            );
            this.navigationService.isNavigationToCheckoutInProcess = false;
          }
        },
        getCheckoutId: this.getCheckoutId,
        widgetId: this.widgetId,
        locale: controllerParams.flowAPI.translations.config.language,
        errorHandler: controllerParams.flowAPI.errorHandler,
        ...(this.viewerContext.isExperimentEnabled(SPECS.MakeOnLoadWaitForLoaderToDisappearInExpress)
          ? {
              onStateChange: ({state}) => {
                if (state !== ExpressCheckoutButtonsState.LOADING) {
                  this.handlePaymentMethodsInit();
                }
              },
            }
          : {
              onLoad: this.handlePaymentMethodsInit,
            }),
        origin: this.origin,
      });
    })();
    return this.controller;
  };

  private readonly getCheckoutId = async () => {
    if (isCartV2(this.cartService.cartModel.id)) {
      this.navigationService.isNavigationToCheckoutInProcess = false;
      return this.cartService.cartModel.id;
    }
    const createCheckoutResult = await this.cartService.createCheckout();
    if (
      typeof createCheckoutResult !== 'string' &&
      createCheckoutResult?.error === CreateCheckoutExceptions.siteMustAcceptPayments
    ) {
      await openNoOnlinePaymentsModal(
        this.checkoutNavigationService,
        this.styleSettingsService.isEditorX,
        this.cartService.cartModel,
        this.cartService.estimatedTotals
      );

      this.navigationService.isNavigationToCheckoutInProcess = false;
      return Promise.resolve(undefined);
    } else {
      setTimeout(() => (this.navigationService.isNavigationToCheckoutInProcess = false), 5_000);
      return Promise.resolve(this.cartService.checkoutId);
    }
  };

  private readonly handlePaymentMethodsInit = () => {
    this.areExpressCheckoutButtonsLoaded = true;
    void this.controllerApi.updateComponent();
  };

  private readonly checkIsAllowedToCheckout = () => {
    const ecommerceSettings = this.ecommerceSettingsService.getEcommerceSettings();
    const hasCreatedPaymentMethods = this.ecommerceSettingsService.hasCreatedPaymentMethods;
    const canStoreShip = ecommerceSettings.delivery?.canBusinessShip;
    const isPremium = ecommerceSettings.isPremium;

    const {canCheckout, modalType, shouldNotify} = this.checkoutNavigationService.checkIsAllowedToCheckout({
      hasShippableItems: this.cartService.hasShippableItems,
      isPremium,
      canStoreShip,
      hasCreatedPaymentMethods,
      canShipToDestination: true,
    });

    if (canCheckout && !isPremium) {
      return {canCheckout: false, modalType: ModalType.NoOnlinePayments, shouldNotify};
    }

    return {canCheckout, modalType, shouldNotify};
  };

  private get cashierExpressEnabled() {
    return this.cartService.areAllItemsInStock;
  }

  private readonly handlePaymentMethodError = () => {
    const queryParamsService = new QueryParamsService(this.siteStore);
    const pmName = queryParamsService.getQueryParam('pmName');
    const pmUrl = queryParamsService.getQueryParam('pmUrl') ?? '';

    if (pmName !== undefined && pmName !== '') {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.modalManagerService.modalManger.openErrorWithPaymentMethod({
        pmName: pmName.replace(/\//g, ''),
        pmUrl: pmUrl.replace(/\//g, ''),
      });
    }
  };

  private shouldRequestShipping(): boolean {
    const {shouldShowShipping} = this.styleSettingsService;
    const isPickup = shouldShowShipping && this.orderService.isPickup();
    const hasShippableItems = this.cartService.hasShippableItems;
    return hasShippableItems && !isPickup;
  }

  private getButtonStyle(): ButtonStyle {
    return {
      shape: this.styleSettingsService.dynamicPaymentMethodsShape === DynamicPaymentMethodsShape.pill ? 'pill' : 'rect',
      height: 42,
    };
  }

  private getButtonTheme(): Theme {
    return this.styleSettingsService.dynamicPaymentMethodsTheme === DynamicPaymentMethodsTheme.light ? 'light' : 'dark';
  }

  private shouldShowDynamicPaymentOptions(): boolean {
    return (
      !this.minimumOrderAmountService.shouldDisableCheckout &&
      !this.cartService?.estimatedTotals?.payLater?.total?.convertedAmount &&
      !this.cartService.hasErrorViolations
    );
  }

  private shouldShowExpressCheckout(): boolean {
    const isCardTokenizationCart = this.cartService?.cartModel?.lineItems?.some(
      (lineItem) => lineItem.savePaymentMethod
    );
    return (
      this.styleSettingsService.isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode &&
      this.shouldShowDynamicPaymentOptions() &&
      !isCardTokenizationCart &&
      !this.cartService.cartModel.hasSubscriptionItem
    );
  }

  private getProps() {
    return {
      handlePaymentMethodError: this.handlePaymentMethodError,
      dynamicPaymentOptionsProps: {
        buttonStyle: this.getButtonStyle(),
        getButtonTheme: this.getButtonTheme(),
        shouldRequestShipping: this.shouldRequestShipping(),
        canCheckout: this.checkIsAllowedToCheckout()?.canCheckout,
      },
      shouldShowExpressCheckout: this.shouldShowExpressCheckout(),
      areExpressCheckoutButtonsLoaded: this.areExpressCheckoutButtonsLoaded,
    };
  }

  public toProps(): Partial<ReturnType<CashierExpressStore['getProps']>> {
    if (this.cashierExpressEnabled && this.cartService.estimatedTotals) {
      return this.getProps();
    }

    return {
      handlePaymentMethodError: this.handlePaymentMethodError,
      shouldShowExpressCheckout: false,
      areExpressCheckoutButtonsLoaded: this.areExpressCheckoutButtonsLoaded,
    };
  }
}
